/**
 * RegistrationQr: the job's public self-registration code as a QR image, the
 * public form URL for a token, and the printable poster window (registration-v2
 * mockup frame 1; the wording is strategy Appendix C, "Poster and QR board").
 *
 * The qrcode package is loaded with a dynamic import inside an effect, so a
 * server render (the seg tests) shows a placeholder box whose alt text is the
 * url and never touches a canvas. The token appears only inside the url; this
 * module stores nothing.
 */
import React, { useEffect, useState } from "react";

const PUBLIC_FORM_BASE = "https://nycsuite.io/r/";
// A 1x1 transparent GIF, so the img keeps its box (and its alt text) until the QR is drawn.
const BLANK_PIXEL = "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";

export function registrationUrl(token) {
  return `${PUBLIC_FORM_BASE}${token}`;
}

async function drawQr(url, width) {
  const mod = await import("qrcode");
  const QRCode = mod.default ?? mod;
  return QRCode.toDataURL(url, { width, margin: 2 });
}

export default function RegistrationQr({ url, size = 240, label }) {
  const [src, setSrc] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setSrc(null);
    drawQr(url, size)
      .then((dataUrl) => { if (!cancelled) setSrc(dataUrl); })
      .catch((e) => { console.error("[RegistrationQr]", e); });
    return () => { cancelled = true; };
  }, [url, size]);

  return (
    // data-feedback-private: a grey box in Feedback tab pictures (src/lib/feedback/screenshot.js).
    <figure className="rigv2-regqr" style={{ width: size }} data-feedback-private="registration QR code">
      <img className={`rigv2-regqr-img${src ? "" : " pending"}`} src={src ?? BLANK_PIXEL} width={size} height={size} alt={url} />
      {label ? <figcaption className="rigv2-regqr-label">{label}</figcaption> : null}
    </figure>
  );
}

function escapeHtml(value) {
  const map = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
  return String(value ?? "").replace(/[&<>"']/g, (c) => map[c]);
}

// The poster page (mockup frame 1): portrait, the event title, the host line, a
// large QR, the three Appendix C lines, the footer and a print button. Inline
// styles only; no external resource of any kind.
function posterHtml({ eventTitle, hostLine }) {
  const title = escapeHtml(eventTitle);
  const host = escapeHtml(hostLine);
  return [
    "<!doctype html>",
    '<html lang="en">',
    "<head>",
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1">',
    '<meta name="referrer" content="no-referrer">',
    `<title>${title || "Registration poster"}</title>`,
    "<style>",
    "@page { size: portrait; margin: 14mm; }",
    "html, body { margin: 0; background: #fff; color: #1A1A1A; font-family: system-ui, -apple-system, 'Segoe UI', Helvetica, Arial, sans-serif; }",
    ".bar { position: fixed; top: 12px; right: 12px; }",
    ".bar button { font: inherit; font-size: 14px; font-weight: 500; padding: 8px 16px; border: 1px solid #1A1A1A; border-radius: 8px; background: #1A1A1A; color: #fff; cursor: pointer; }",
    ".bar button:disabled { opacity: 0.5; cursor: default; }",
    ".poster { box-sizing: border-box; min-height: 100vh; display: flex; flex-direction: column; align-items: center; text-align: center; padding: 56px 36px 28px; }",
    ".title { font-size: 44px; font-weight: 500; line-height: 1.1; letter-spacing: -0.02em; margin: 0 0 8px; }",
    ".host { font-size: 18px; color: #666; margin: 0 0 36px; }",
    ".qr { display: block; width: 360px; height: 360px; border: 1px solid #DDD8D1; background: #fff; }",
    ".lines { margin: 36px 0 0; font-size: 22px; line-height: 1.5; font-weight: 300; }",
    ".lines b { display: block; font-weight: 500; }",
    ".foot { margin-top: auto; padding-top: 36px; font-size: 12px; letter-spacing: 0.12em; text-transform: uppercase; color: #999; }",
    "@media print { .bar { display: none; } .poster { min-height: 0; height: 100vh; overflow: hidden; } }",
    "</style>",
    "</head>",
    "<body>",
    '<div class="bar"><button id="print-btn" type="button" disabled>Print</button></div>',
    '<main class="poster">',
    `<h1 class="title">${title}</h1>`,
    `<p class="host">${host}</p>`,
    `<img id="qr" class="qr" src="${BLANK_PIXEL}" width="360" height="360" alt="Registration QR code">`,
    '<p class="lines"><b>Register to be 3D scanned.</b>Scan this code on your phone.<br>Show your pass at the capture station.</p>',
    '<footer class="foot">nycsuite.io</footer>',
    "</main>",
    "</body>",
    "</html>",
  ].join("\n");
}

export function openPosterWindow({ url, eventTitle, hostLine }) {
  // Opened synchronously from the click so popup blockers allow it. The QR is
  // drawn here and passed in as a data URL a moment later; the print button
  // stays disabled until it is in place.
  const win = window.open("", "_blank");
  if (!win) return null;
  const d = win.document;
  d.open();
  d.write(posterHtml({ eventTitle, hostLine }));
  d.close();
  const printBtn = d.getElementById("print-btn");
  if (printBtn) printBtn.addEventListener("click", () => win.print());
  drawQr(url, 720)
    .then((dataUrl) => {
      const img = d.getElementById("qr");
      if (img) img.src = dataUrl;
      if (printBtn) printBtn.disabled = false;
    })
    .catch((e) => { console.error("[RegistrationQr] poster", e); });
  return win;
}
